'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import {
  Shield,
  CheckCircle,
  AlertTriangle,
  Zap,
  Lock,
  Cpu,
  Activity,
  UserCheck,
  RefreshCw,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

type AuthStage = 'idle' | 'google-auth' | 'verifying' | 'authorized' | 'denied' | 'timeout';

export default function BiometricLockScreen() {
  const router = useRouter();
  const { user, logIntrusionAttempt } = useAuth();

  const [stage, setStage] = useState<AuthStage>('idle');
  const [particles, setParticles] = useState<
    { id: string; x: number; y: number; size: number; opacity: number }[]
  >([]);
  const [statusMessage, setStatusMessage] = useState('Awaiting authentication...');
  const [alphaValue] = useState('1 / 137.035999084');
  const [systemTime, setSystemTime] = useState('');
  const [threatLevel, setThreatLevel] = useState('NOMINAL');
  const [intrusionAttempt, setIntrusionAttempt] = useState(false);
  const verifyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const authInitiatedRef = useRef(false);

  // ── System clock ──────────────────────────────────────────────────────────
  useEffect(() => {
    const update = () => {
      const now = new Date();
      setSystemTime(now.toISOString().replace('T', ' ').substring(0, 19));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  // ── Particles (client-only) ───────────────────────────────────────────────
  useEffect(() => {
    setParticles(
      Array.from({ length: 20 }, (_, i) => ({
        id: `particle-${i}`,
        x: (i * 137) % 100,
        y: (i * 73) % 100,
        size: (i % 3) + 1,
        opacity: ((i % 5) + 1) * 0.08,
      }))
    );
  }, []);

  // ── Cleanup timeout on unmount ────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (verifyTimeoutRef.current) clearTimeout(verifyTimeoutRef.current);
    };
  }, []);

  // ── Auto-redirect if sovereign user already signed in ─────────────────────
  useEffect(() => {
    if (user && !authInitiatedRef.current) {
      authInitiatedRef.current = true;
      handlePostGoogleAuth(user);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Handle result after Google OAuth returns ──────────────────────────────
  const handlePostGoogleAuth = async (userObj: any) => {
    setStage('verifying');
    setStatusMessage('Verifying sovereign identity...');

    // Safety timeout — if verification stalls for >8s, reset to idle
    verifyTimeoutRef.current = setTimeout(() => {
      setStage('timeout');
      setStatusMessage('Verification timed out. Tap to retry.');
      authInitiatedRef.current = false;
    }, 8000);

    try {
      await new Promise(r => setTimeout(r, 400));

      const email: string = (userObj?.email || '').toLowerCase().trim();
      const isSovereign = email === 'yigitmiko2734@gmail.com';

      if (verifyTimeoutRef.current) {
        clearTimeout(verifyTimeoutRef.current);
        verifyTimeoutRef.current = null;
      }

      if (!isSovereign) {
        setIntrusionAttempt(true);
        setThreatLevel('CRITICAL');
        setStatusMessage('⚠ INTRUSION BLOCKED — α=1/137 GATE ENGAGED — UNAUTHORIZED ENTITY DETECTED');
        toast.error('Access denied. Unauthorized identity. α=1/137 gate engaged.');
        // Log intrusion silently — don't await to avoid blocking UI
        logIntrusionAttempt(
          email,
          'Non-sovereign identity — α=1/137 gate engaged. Permanently blocked.'
        ).catch(() => {});
        const supabase = createClient();
        supabase.auth.signOut().catch(() => {});
        setTimeout(() => {
          setIntrusionAttempt(false);
          setThreatLevel('NOMINAL');
          setStage('idle');
          setStatusMessage('Awaiting authentication...');
          authInitiatedRef.current = false;
        }, 5000);
        return;
      }

      setStage('authorized');
      setStatusMessage('IDENTITY CONFIRMED — Sovereign access granted.');
      toast.success('Sovereign access granted. Jarvis online.');
      setTimeout(() => router.push('/command-center'), 800);
    } catch {
      if (verifyTimeoutRef.current) {
        clearTimeout(verifyTimeoutRef.current);
        verifyTimeoutRef.current = null;
      }
      setStage('timeout');
      setStatusMessage('Verification error. Tap to retry.');
      authInitiatedRef.current = false;
    }
  };

  // ── Google OAuth sign-in ──────────────────────────────────────────────────
  const initiateGoogleAuth = async () => {
    if (stage === 'google-auth' || stage === 'verifying' || stage === 'authorized') return;

    setStage('google-auth');
    setStatusMessage('Redirecting to Google...');

    // Timeout protection: if OAuth redirect doesn't happen in 10s, reset
    const oauthTimeout = setTimeout(() => {
      setStage('idle');
      setStatusMessage('Google sign-in timed out. Try again.');
      toast.error('Sign-in timed out — please try again.');
    }, 10000);

    const supabase = createClient();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/sign-in-biometric-lock`,
      },
    });

    clearTimeout(oauthTimeout);

    if (error) {
      setStage('idle');
      setStatusMessage('Google authentication failed. Try again.');
      toast.error('Google sign-in failed: ' + error.message);
    }
    // If no error, browser will redirect — no further action needed
  };

  const handleRetry = () => {
    setStage('idle');
    setStatusMessage('Awaiting authentication...');
    authInitiatedRef.current = false;
  };

  return (
    <div className="min-h-screen bg-sovereign-bg flex items-center justify-center relative overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 grid-sovereign opacity-30" />
      <div className="absolute inset-0 scanline" />

      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-sovereign-cyan/3 blur-[120px] pointer-events-none" />
      <div className="absolute top-1/4 right-1/4 w-[300px] h-[300px] rounded-full bg-purple-600/5 blur-[80px] pointer-events-none" />

      {/* Floating particles */}
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full bg-sovereign-cyan animate-float pointer-events-none"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            opacity: p.opacity,
            animationDelay: `${p.id.split('-')[1]}s`,
          }}
        />
      ))}

      {/* Top system bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-8 py-4 border-b border-sovereign-cyan/10">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <AppLogo size={24} />
            <span className="font-mono text-[11px] text-sovereign-cyan/50 tracking-[0.3em] uppercase">
              JARVIS SOVEREIGN CORE
            </span>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-1.5">
            <Zap size={10} className="text-sovereign-cyan/30" />
            <span className="font-mono text-[10px] text-sovereign-cyan/30 tracking-widest">
              α = {alphaValue}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                threatLevel === 'CRITICAL' ? 'bg-red-400' : 'bg-green-400'
              }`}
            />
            <span
              className={`font-mono text-[10px] tracking-widest ${
                threatLevel === 'CRITICAL' ? 'text-red-400' : 'text-green-400/60'
              }`}
            >
              THREAT: {threatLevel}
            </span>
          </div>
          <span className="font-mono text-[10px] text-sovereign-cyan/30">{systemTime}</span>
        </div>
      </div>

      {/* Main auth card */}
      <div className="relative z-10 w-full max-w-lg mx-4">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Lock size={14} className="text-sovereign-cyan/40" />
            <span className="font-mono text-[10px] text-sovereign-cyan/40 tracking-[0.3em] uppercase">
              FORTRESS PROTOCOL
            </span>
            <Lock size={14} className="text-sovereign-cyan/40" />
          </div>
          <h1 className="text-3xl font-700 text-sovereign-cyan text-glow-cyan tracking-tight mb-2">
            Identity Verification
          </h1>
          <p className="text-[13px] text-sovereign-cyan/40 font-mono">
            Welcome back. Initiating authentication sequence.
          </p>
        </div>

        {/* Auth display */}
        <div
          className="relative rounded-2xl p-8 mb-6 cyber-border overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, hsl(220, 70%, 8%) 0%, hsl(222, 84%, 6%) 100%)',
          }}
        >
          {/* Center icon */}
          <div className="flex justify-center mb-8">
            <div className="relative w-32 h-32 flex items-center justify-center">
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
                <defs>
                  <linearGradient id="ring-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="hsl(192, 100%, 50%)" stopOpacity="0.8" />
                    <stop offset="50%" stopColor="hsl(262, 83%, 58%)" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="hsl(192, 100%, 50%)" stopOpacity="0.2" />
                  </linearGradient>
                </defs>
                <circle
                  cx="100"
                  cy="100"
                  r="88"
                  fill="none"
                  stroke="url(#ring-gradient)"
                  strokeWidth="1.5"
                  strokeDasharray="4 8"
                  className={stage === 'google-auth' || stage === 'verifying' || stage === 'authorized' ? 'animate-spin-slow' : ''}
                />
                {stage === 'authorized' && (
                  <>
                    <circle
                      cx="100"
                      cy="100"
                      r="88"
                      fill="none"
                      stroke="hsl(142, 71%, 45%)"
                      strokeWidth="2"
                      strokeOpacity="0.6"
                    />
                    <path
                      d="M 75 100 L 92 117 L 125 83"
                      fill="none"
                      stroke="hsl(142, 71%, 45%)"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </>
                )}
              </svg>

              {/* Center icon based on stage */}
              <div className="relative z-10">
                {stage === 'idle' && <Shield size={40} className="text-sovereign-cyan/60" />}
                {stage === 'google-auth' && <Cpu size={40} className="text-sovereign-cyan animate-pulse" />}
                {stage === 'verifying' && <Activity size={40} className="text-sovereign-cyan animate-pulse" />}
                {stage === 'authorized' && <CheckCircle size={40} className="text-green-400" />}
                {(stage === 'denied' || intrusionAttempt) && <AlertTriangle size={40} className="text-red-400 animate-pulse" />}
                {stage === 'timeout' && <RefreshCw size={40} className="text-amber-400" />}
              </div>
            </div>
          </div>

          {/* Status message */}
          <div className="text-center mb-6">
            <p
              className={`font-mono text-[12px] tracking-wider ${
                intrusionAttempt
                  ? 'text-red-400'
                  : stage === 'authorized' ?'text-green-400'
                  : stage === 'timeout' ?'text-amber-400' :'text-sovereign-cyan/60'
              }`}
            >
              {statusMessage}
            </p>
          </div>

          {/* System metrics */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: 'NEURAL LINK', value: stage === 'verifying' || stage === 'authorized' ? 'ACTIVE' : 'STANDBY', icon: <Cpu size={10} /> },
              { label: 'IDENTITY GATE', value: stage === 'authorized' ? 'OPEN' : 'LOCKED', icon: <Shield size={10} /> },
              { label: 'THREAT LEVEL', value: threatLevel, icon: <Activity size={10} /> },
            ].map((metric) => (
              <div
                key={metric.label}
                className="rounded-lg p-2.5 border border-sovereign-cyan/10 bg-sovereign-bg/50 text-center"
              >
                <div className="flex items-center justify-center gap-1 mb-1 text-sovereign-cyan/30">
                  {metric.icon}
                  <span className="font-mono text-[8px] tracking-widest">{metric.label}</span>
                </div>
                <span
                  className={`font-mono text-[10px] font-600 tracking-wider ${
                    metric.value === 'ACTIVE' || metric.value === 'OPEN' ?'text-green-400'
                      : metric.value === 'CRITICAL' ?'text-red-400' :'text-sovereign-cyan/50'
                  }`}
                >
                  {metric.value}
                </span>
              </div>
            ))}
          </div>

          {/* Auth button */}
          {stage === 'timeout' ? (
            <button
              onClick={handleRetry}
              className="w-full py-3.5 rounded-xl font-mono text-[13px] tracking-widest font-600 transition-all duration-200 flex items-center justify-center gap-3 bg-amber-500/15 border border-amber-500/40 text-amber-400 hover:bg-amber-500/25"
            >
              <RefreshCw size={16} />
              RETRY AUTHENTICATION
            </button>
          ) : (
            <button
              onClick={initiateGoogleAuth}
              disabled={stage === 'google-auth' || stage === 'verifying' || stage === 'authorized'}
              className={`w-full py-3.5 rounded-xl font-mono text-[13px] tracking-widest font-600 transition-all duration-200 flex items-center justify-center gap-3 ${
                stage === 'authorized' ?'bg-green-500/15 border border-green-500/40 text-green-400'
                  : intrusionAttempt
                  ? 'bg-red-500/15 border border-red-500/40 text-red-400 cursor-not-allowed'
                  : stage === 'google-auth' || stage === 'verifying'
                  ? 'bg-sovereign-cyan/10 border border-sovereign-cyan/30 text-sovereign-cyan/50 cursor-wait' :'bg-sovereign-cyan/10 border border-sovereign-cyan/30 text-sovereign-cyan hover:bg-sovereign-cyan/20 hover:border-sovereign-cyan/60 active:scale-[0.98]'
              }`}
            >
              {stage === 'idle' && (
                <>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  SIGN IN WITH GOOGLE
                </>
              )}
              {stage === 'google-auth' && (
                <>
                  <div className="w-4 h-4 border-2 border-sovereign-cyan/30 border-t-sovereign-cyan rounded-full animate-spin" />
                  REDIRECTING...
                </>
              )}
              {stage === 'verifying' && (
                <>
                  <div className="w-4 h-4 border-2 border-sovereign-cyan/30 border-t-sovereign-cyan rounded-full animate-spin" />
                  VERIFYING IDENTITY...
                </>
              )}
              {stage === 'authorized' && (
                <>
                  <CheckCircle size={16} />
                  ACCESS GRANTED
                </>
              )}
              {intrusionAttempt && (
                <>
                  <AlertTriangle size={16} />
                  INTRUSION BLOCKED
                </>
              )}
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <UserCheck size={10} className="text-sovereign-cyan/20" />
            <span className="font-mono text-[9px] text-sovereign-cyan/20 tracking-widest">
              SOVEREIGN IDENTITY PROTOCOL · α=1/137
            </span>
          </div>
          <p className="font-mono text-[9px] text-sovereign-cyan/15">
            Unauthorized access attempts are logged and permanently blocked.
          </p>
        </div>
      </div>
    </div>
  );
}